import React, { useState, useEffect, useCallback } from 'react';
import whiteLabelService from '../../services/whiteLabelService';
import { config } from '../../config';
import { initializePaddle } from '../../utils/paddle';
import { usePaymentProvider } from '../../hooks/usePaymentProvider';
import PaddlePaymentForm from '../Paddle/PaddlePaymentForm';
import '../../pages/scss/BillingTable.scss'; // reuse the billing checkout UI
import './scss/WhiteLabelPaymentForm.scss';

/**
 * Paddle equivalent of WhiteLabelPaymentForm. Same billing-cycle UI and props
 * ({ onSuccess, onError }); the payment step is the reusable one-screen
 * PaddlePaymentForm — plan summary + inline card fields shown together, then
 * activation is verified (webhook-driven) before onSuccess fires.
 */
const PaddleWhiteLabelPaymentForm = ({ onSuccess, onError }) => {
  const { paddleClientToken, paddleEnvironment } = usePaymentProvider();
  const [error, setError] = useState('');
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [paddleReady, setPaddleReady] = useState(false);
  // When true, the one-screen PaddlePaymentForm replaces the cycle picker and
  // handles transaction creation + inline card fields + activation verify.
  const [showPayment, setShowPayment] = useState(false);

  const pricingInfo = whiteLabelService.getPricingInfo();

  // Initialize Paddle.js once the client token is available.
  useEffect(() => {
    if (!paddleClientToken) return;
    initializePaddle(paddleClientToken, paddleEnvironment)
      .then(() => setPaddleReady(true))
      .catch((err) => {
        console.error('Failed to initialize Paddle:', err);
        setError('Failed to load payment system');
        onError?.('Failed to load payment system');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paddleClientToken, paddleEnvironment]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setError('');
    // Move into the one-screen payment step; PaddlePaymentForm owns the
    // create-transaction call + inline card fields + activation verify.
    setShowPayment(true);
  };

  // create-transaction call the PaddlePaymentForm invokes on mount.
  const createWhiteLabelTransaction = useCallback(async () => {
    const response = await fetch(`${config.API_URI}/v1/paddle/create-transaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ type: 'white_label_addon', billingCycle })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to create transaction');
    }
    return { transactionId: data.transactionId, amountDollars: pricingInfo[billingCycle].amount };
  }, [billingCycle, pricingInfo]);

  const cancelPayment = () => {
    setShowPayment(false);
  };

  if (showPayment) {
    return (
      <div className="white-label-payment-form">
        <PaddlePaymentForm
          type="white_label"
          createTransaction={createWhiteLabelTransaction}
          title={`White Label — ${billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}`}
          amountDollars={pricingInfo[billingCycle].amount}
          billingNote={`Billed ${billingCycle === 'yearly' ? 'annually' : 'monthly'} • Paddle handles payment, tax, and invoicing.`}
          verifyType="white_label_addon"
          frameTarget="paddle-inline-wl"
          onSuccess={() => onSuccess?.()}
          onCancel={cancelPayment}
        />
      </div>
    );
  }

  // Billing-cycle picker styled to match the billing page checkout: the .seg
  // pill toggle + .opt option cards + .pay-btn, inside the .billing-container
  // token context so it inherits the exact minimal-mono look.
  return (
    <form onSubmit={handleSubmit} className="billing-container wl-addon-checkout">
      <div className="co-card">
        <h3>White Label add-on</h3>
        <p className="hint">Choose how you’d like to be billed. Cancel anytime.</p>

        <div className="lbl">Billing cycle</div>
        <div className="opts">
          <div className={`opt ${billingCycle === 'monthly' ? 'sel' : ''}`} onClick={() => setBillingCycle('monthly')}>
            <div className="pn">Monthly</div>
            <div className="pp">${pricingInfo.monthly.amount} <span className="pnote">/mo</span></div>
          </div>
          <div className={`opt ${billingCycle === 'yearly' ? 'sel' : ''}`} onClick={() => setBillingCycle('yearly')}>
            <div className="pn">Yearly {pricingInfo.yearly.savings && <span className="pnote">· save {pricingInfo.yearly.savings}</span>}</div>
            <div className="pp">${pricingInfo.yearly.amount} <span className="pnote">/yr</span></div>
            <div className="pnote">${pricingInfo.yearly.monthlyEquivalent}/mo billed annually</div>
          </div>
        </div>

        {error && <div className="co-error">{error}</div>}
      </div>

      <div className="co-card sum">
        <h4>Order summary</h4>
        <div className="sline"><span>White Label · {billingCycle === 'yearly' ? 'yearly' : 'monthly'}</span><span className="amt">${pricingInfo[billingCycle].amount}</span></div>
        <div className="sline muted"><span>Tax</span><span className="amt">+ calculated at checkout</span></div>
        <div className="stot">
          <div><div className="lab">Total</div><div className="cyc">plus applicable tax</div></div>
          <div className="big">${pricingInfo[billingCycle].amount}<span className="plus-tax"> + tax</span></div>
        </div>
        <button type="submit" className="pay-btn" disabled={!paddleReady}>
          {paddleReady ? 'Continue to Paddle' : 'Loading…'}
        </button>
        <div className="trust">Secured &amp; encrypted · Cancel anytime</div>
      </div>
    </form>
  );
};

export default PaddleWhiteLabelPaymentForm;
